const controllers = new WeakMap();
let openController;
let generatedId = 0;

const nextId = () => `SupportCalendar-${++generatedId}`;
const pad = (value) => String(value).padStart(2, '0');
const toIso = (date) => `${String(date.getFullYear()).padStart(4, '0')}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const makeDate = (year, month, day) => {
  const date = new Date(0);
  date.setHours(0, 0, 0, 0);
  date.setFullYear(year, month, day);
  return date;
};
const localToday = () => {
  const now = new Date();
  return makeDate(now.getFullYear(), now.getMonth(), now.getDate());
};
const fromIso = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return null;
  if (Number(match[1]) < 1) return null;
  const date = makeDate(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return toIso(date) === value ? date : null;
};
const addDays = (date, amount) => makeDate(date.getFullYear(), date.getMonth(), date.getDate() + amount);
const addMonths = (date, amount) => {
  const day = date.getDate();
  const target = makeDate(date.getFullYear(), date.getMonth() + amount, 1);
  const lastDay = makeDate(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, lastDay));
  return target;
};

class SupportCalendar {
  constructor(input) {
    this.input = input;
    this.today = localToday();
    this.input.max = toIso(this.today);
    this.viewDate = fromIso(input.value) || this.today;
    this.focusDate = this.viewDate;
    this.abortController = new AbortController();
    this.build();
    this.bind();
    this.sync();
  }

  build() {
    const id = this.input.id || nextId();
    const root = document.createElement('div');
    const trigger = document.createElement('button');
    const value = document.createElement('span');
    const icon = document.createElement('span');
    const panel = document.createElement('div');
    const header = document.createElement('div');
    const previous = this.iconButton('Previous month', '<path d="m14.5 5-5 7 5 7"/>');
    const next = this.iconButton('Next month', '<path d="m9.5 5 5 7-5 7"/>');
    const jump = document.createElement('div');
    const month = document.createElement('button');
    const months = document.createElement('div');
    const year = document.createElement('button');
    const years = document.createElement('div');
    const calendar = document.createElement('table');
    const weekdays = document.createElement('thead');
    const weekdayRow = document.createElement('tr');
    const grid = document.createElement('tbody');
    const instructions = document.createElement('p');

    this.input.id = id;
    root.className = 'support-calendar';
    trigger.className = 'support-calendar__trigger';
    trigger.type = 'button';
    trigger.id = `${id}-trigger`;
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', `${id}-dialog`);
    trigger.setAttribute('aria-expanded', 'false');
    value.className = 'support-calendar__value';
    icon.className = 'support-calendar__trigger-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M7 3v3m10-3v3M4.5 9.5h15M6.25 5h11.5A1.75 1.75 0 0 1 19.5 6.75v11A1.75 1.75 0 0 1 17.75 19.5H6.25a1.75 1.75 0 0 1-1.75-1.75v-11A1.75 1.75 0 0 1 6.25 5Z"/></svg>';
    panel.className = 'support-calendar__panel';
    panel.id = `${id}-dialog`;
    panel.role = 'dialog';
    panel.setAttribute('aria-modal', 'false');
    panel.hidden = true;
    panel.inert = true;
    header.className = 'support-calendar__header';
    jump.className = 'support-calendar__jump';
    month.className = 'support-calendar__month';
    month.type = 'button';
    month.setAttribute('aria-expanded', 'false');
    month.setAttribute('aria-controls', `${id}-months`);
    months.id = `${id}-months`;
    months.className = 'support-calendar__months';
    months.setAttribute('role', 'group');
    months.setAttribute('aria-label', 'Choose month');
    months.hidden = true;
    year.className = 'support-calendar__year';
    year.type = 'button';
    year.setAttribute('aria-expanded', 'false');
    year.setAttribute('aria-controls', `${id}-years`);
    years.id = `${id}-years`;
    years.className = 'support-calendar__months support-calendar__years';
    years.setAttribute('role', 'group');
    years.setAttribute('aria-label', 'Choose year');
    years.hidden = true;
    calendar.className = 'support-calendar__calendar';
    weekdays.className = 'support-calendar__weekdays';
    grid.className = 'support-calendar__grid';
    instructions.className = 'support-calendar__instructions';
    instructions.id = `${id}-instructions`;
    instructions.textContent = 'Use the arrow keys to choose a date. Page Up and Page Down change month. Hold Shift to change year.';
    panel.setAttribute('aria-describedby', instructions.id);

    for (let index = 0; index < 12; index++) {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'support-calendar__month-option';
      option.dataset.month = String(index);
      option.textContent = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(makeDate(2024, index, 1));
      months.append(option);
    }
    ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].forEach((label) => {
      const day = document.createElement('th');
      day.scope = 'col';
      day.textContent = label;
      weekdayRow.append(day);
    });
    weekdays.append(weekdayRow);
    calendar.append(weekdays, grid);

    const label = this.input.labels?.[0];
    if (label) {
      label.id ||= `${id}-label`;
      trigger.setAttribute('aria-labelledby', `${label.id} ${id}-value`);
      panel.setAttribute('aria-labelledby', label.id);
    } else {
      trigger.setAttribute('aria-label', 'Date of purchase');
      panel.setAttribute('aria-label', 'Choose date of purchase');
    }
    value.id = `${id}-value`;
    this.input.before(root);
    root.append(this.input, trigger);
    trigger.append(value, icon);
    jump.append(month, year);
    header.append(previous, jump, next);
    panel.append(header, months, years, calendar, instructions);
    document.body.append(panel);
    this.input.classList.add('support-calendar__native');
    this.input.tabIndex = -1;

    this.root = root;
    this.trigger = trigger;
    this.value = value;
    this.panel = panel;
    this.previous = previous;
    this.next = next;
    this.month = month;
    this.months = months;
    this.calendar = calendar;
    this.year = year;
    this.years = years;
    this.grid = grid;
    this.fieldset = this.input.closest('fieldset');
  }

  iconButton(label, path) {
    const button = document.createElement('button');
    button.className = 'support-calendar__nav';
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${path}</svg>`;
    return button;
  }

  bind() {
    const { signal } = this.abortController;
    this.trigger.addEventListener('click', () => this.toggle(), { signal });
    this.trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.open();
      }
    }, { signal });
    this.previous.addEventListener('click', () => this.navigateHeader(-1), { signal });
    this.next.addEventListener('click', () => this.navigateHeader(1), { signal });
    this.month.addEventListener('click', () => this.showMonths(this.months.hidden), { signal });
    this.months.addEventListener('click', (event) => {
      const button = event.target.closest('[data-month]');
      if (!button || button.disabled) return;
      this.jumpTo(this.viewDate.getFullYear(), Number(button.dataset.month));
      this.showMonths(false);
      this.focusCurrentDay();
    }, { signal });
    this.year.addEventListener('click', () => this.showYears(this.years.hidden), { signal });
    this.years.addEventListener('click', (event) => {
      const button = event.target.closest('[data-year]');
      if (!button || button.disabled) return;
      this.jumpTo(Number(button.dataset.year), this.viewDate.getMonth());
      this.showYears(false);
      this.showMonths(true);
    }, { signal });
    this.grid.addEventListener('click', (event) => {
      const button = event.target.closest('[data-date]');
      if (button && !button.disabled) this.choose(fromIso(button.dataset.date));
    }, { signal });
    this.grid.addEventListener('focusin', (event) => {
      const button = event.target.closest('[data-date]');
      const date = button ? fromIso(button.dataset.date) : null;
      if (date && !button.disabled) this.focusDate = date;
    }, { signal });
    this.panel.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.input.addEventListener('input', () => this.sync(), { signal });
    this.input.addEventListener('change', () => this.sync(), { signal });
    this.input.addEventListener('invalid', (event) => {
      event.preventDefault();
      this.sync();
      queueMicrotask(() => this.trigger.focus({ preventScroll: true }));
    }, { signal });
    this.input.addEventListener('focus', () => queueMicrotask(() => this.trigger.focus({ preventScroll: true })), { signal });
    this.input.form?.addEventListener('reset', () => queueMicrotask(() => {
      this.viewDate = this.today;
      this.focusDate = this.today;
      this.close(false);
      this.sync();
    }), { signal });
    this.input.form?.addEventListener('submit', () => this.close(false), { signal });
    window.addEventListener('resize', () => this.reposition(), { signal, passive: true });
    window.addEventListener('scroll', () => this.reposition(), { signal, passive: true, capture: true });
    window.visualViewport?.addEventListener('resize', () => this.reposition(), { signal, passive: true });
    window.visualViewport?.addEventListener('scroll', () => this.reposition(), { signal, passive: true });

    this.observer = new MutationObserver(() => {
      this.trigger.setAttribute('aria-invalid', this.input.getAttribute('aria-invalid') || 'false');
      this.syncDisabled();
    });
    this.observer.observe(this.input, { attributes: true, attributeFilter: ['disabled', 'required', 'aria-invalid'] });
    if (this.fieldset) this.observer.observe(this.fieldset, { attributes: true, attributeFilter: ['disabled'] });
  }

  sync() {
    const selected = fromIso(this.input.value);
    if (selected) {
      this.viewDate = selected;
      this.focusDate = selected;
      this.value.textContent = new Intl.DateTimeFormat('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric',
      }).format(selected);
      this.root.dataset.hasValue = 'true';
    } else {
      this.value.textContent = 'Choose a date';
      delete this.root.dataset.hasValue;
    }
    this.trigger.setAttribute('aria-invalid', this.input.getAttribute('aria-invalid') || 'false');
    this.syncDisabled();
    if (!this.panel.hidden) this.render();
  }

  syncDisabled() {
    this.trigger.disabled = this.input.matches(':disabled');
    if (this.trigger.disabled) this.close(false);
  }

  toggle() {
    if (!this.isOpen) this.open();
    else this.close();
  }

  open() {
    if (this.trigger.disabled || this.isOpen) return;
    if (openController && openController !== this) openController.close(false);
    const selected = fromIso(this.input.value);
    this.focusDate = selected || this.today;
    this.viewDate = this.focusDate;
    this.showMonths(false);
    this.isOpen = true;
    this.panel.hidden = false;
    this.panel.inert = false;
    this.root.dataset.open = 'true';
    this.trigger.setAttribute('aria-expanded', 'true');
    openController = this;
    this.render();
    this.reposition();
    this.animatePanel(true);
    this.focusCurrentDay();
  }

  close(returnFocus = true) {
    if (!this.isOpen) return;
    this.isOpen = false;
    delete this.root.dataset.open;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.inert = true;
    if (openController === this) openController = undefined;
    if (returnFocus) this.trigger.focus({ preventScroll: true });
    this.animatePanel(false);
  }

  animatePanel(opening) {
    this.panelAnimation?.cancel();
    const finish = () => {
      if (!opening && !this.isOpen) this.panel.hidden = true;
    };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !this.panel.animate) {
      finish();
      return;
    }
    const animation = this.panel.animate([
      opening ? { opacity: 0, transform: 'translate3d(0, -8px, 0) scale(0.985)' } : { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
      opening ? { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' } : { opacity: 0, transform: 'translate3d(0, -6px, 0) scale(0.992)' },
    ], {
      duration: opening ? 160 : 110,
      easing: opening ? 'cubic-bezier(0.23, 1, 0.32, 1)' : 'cubic-bezier(0.215, 0.61, 0.355, 1)',
      fill: 'both',
    });
    this.panelAnimation = animation;
    animation.onfinish = () => {
      if (this.panelAnimation !== animation) return;
      finish();
      animation.cancel();
      this.panelAnimation = null;
    };
  }

  reposition() {
    if (this.panel.hidden) return;
    const rect = this.trigger.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportLeft = viewport?.offsetLeft || 0;
    const viewportWidth = viewport?.width || window.innerWidth;
    const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
    const panelRect = this.panel.getBoundingClientRect();
    const gap = 8;
    const edge = viewportWidth <= 340 ? 2 : 4;
    const preferredTop = viewportBottom - rect.bottom >= panelRect.height + gap
      ? rect.bottom + gap
      : rect.top - panelRect.height - gap;
    const maxTop = Math.max(viewportTop + edge, viewportBottom - panelRect.height - edge);
    const top = Math.min(Math.max(preferredTop, viewportTop + edge), maxTop);
    const left = Math.min(
      Math.max(viewportLeft + edge, rect.left),
      viewportLeft + viewportWidth - panelRect.width - edge,
    );
    this.panel.style.setProperty('--support-calendar-top', `${Math.round(top)}px`);
    this.panel.style.setProperty('--support-calendar-left', `${Math.round(left)}px`);
  }

  render(direction = 0, mode = 'pointer') {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();
    this.previous.setAttribute('aria-label', 'Previous month');
    this.next.setAttribute('aria-label', 'Next month');
    this.month.textContent = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(this.viewDate);
    this.month.setAttribute('aria-label', `Change month, ${this.month.textContent}`);
    this.months.querySelectorAll('[data-month]').forEach((button) => {
      const index = Number(button.dataset.month);
      button.disabled = year === this.today.getFullYear() && index > this.today.getMonth();
      button.setAttribute('aria-pressed', String(index === month));
    });
    this.previous.disabled = year === 1 && month === 0;
    this.year.textContent = String(year);
    this.year.setAttribute('aria-label', `Change year, ${year}`);
    this.next.disabled = year === this.today.getFullYear() && month === this.today.getMonth();
    const first = makeDate(year, month, 1);
    const offset = (first.getDay() + 6) % 7;
    const start = addDays(first, -offset);
    const selectedIso = this.input.value;
    const todayIso = toIso(this.today);
    const fragment = document.createDocumentFragment();
    let row;

    for (let index = 0; index < 42; index++) {
      if (index % 7 === 0) {
        row = document.createElement('tr');
        fragment.append(row);
      }
      const date = addDays(start, index);
      const iso = toIso(date);
      const cell = document.createElement('td');
      const button = document.createElement('button');
      const isOutside = date.getMonth() !== month;
      button.className = 'support-calendar__day';
      button.type = 'button';
      button.dataset.date = iso;
      button.textContent = String(date.getDate());
      button.disabled = date.getFullYear() < 1 || date > this.today;
      button.tabIndex = iso === toIso(this.focusDate) ? 0 : -1;
      button.setAttribute('aria-label', new Intl.DateTimeFormat('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      }).format(date));
      button.setAttribute('aria-pressed', String(iso === selectedIso));
      if (isOutside) button.dataset.outside = 'true';
      if (iso === todayIso) {
        button.dataset.today = 'true';
        button.setAttribute('aria-current', 'date');
      }
      cell.append(button);
      row.append(cell);
    }
    this.grid.replaceChildren(fragment);
    if (direction && !matchMedia('(prefers-reduced-motion: reduce)').matches && this.grid.animate) {
      this.grid.animate(
        mode === 'keyboard'
          ? [{ opacity: 0.35 }, { opacity: 1 }]
          : [{ opacity: 0.35, transform: `translate3d(${direction * 12}px, 0, 0)` }, { opacity: 1, transform: 'translate3d(0, 0, 0)' }],
        { duration: 150, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' },
      );
    }
  }

  changeMonth(amount, mode = 'pointer') {
    const target = addMonths(makeDate(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1), amount);
    if (target.getFullYear() < 1) return;
    if (target > makeDate(this.today.getFullYear(), this.today.getMonth(), 1)) return;
    this.viewDate = target;
    const focusedDay = Math.min(this.focusDate.getDate(), makeDate(target.getFullYear(), target.getMonth() + 1, 0).getDate());
    this.focusDate = makeDate(target.getFullYear(), target.getMonth(), focusedDay);
    if (this.focusDate > this.today) this.focusDate = this.today;
    this.render(Math.sign(amount), mode);
    if (mode === 'keyboard') this.focusCurrentDay();
  }

  jumpTo(year, month) {
    if (!Number.isInteger(year) || year < 1 || year > this.today.getFullYear()) {
      this.render();
      return;
    }
    const currentMonth = makeDate(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1);
    let target = makeDate(year, month, 1);
    const latestMonth = makeDate(this.today.getFullYear(), this.today.getMonth(), 1);
    if (target > latestMonth) target = latestMonth;
    const direction = Math.sign(target - currentMonth);
    this.viewDate = target;
    this.focusDate = makeDate(year, month, Math.min(this.focusDate.getDate(), makeDate(year, month + 1, 0).getDate()));
    if (this.focusDate > this.today) this.focusDate = this.today;
    this.render(direction, 'pointer');
  }

  showMonths(open) {
    if (!this.years.hidden) this.showYears(false);
    this.months.hidden = !open;
    this.calendar.hidden = open;
    this.month.setAttribute('aria-expanded', String(open));
    if (open) this.months.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
    this.reposition();
  }

  showYears(open) {
    this.years.hidden = !open;
    this.months.hidden = true;
    this.month.setAttribute('aria-expanded', 'false');
    this.month.hidden = open;
    this.calendar.hidden = open;
    this.year.setAttribute('aria-expanded', String(open));
    if (open) {
      this.yearStart = Math.floor((this.viewDate.getFullYear() - 1) / 12) * 12 + 1;
      this.renderYears();
      this.years.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
    } else this.render();
    this.reposition();
  }

  renderYears() {
    const end = this.yearStart + 11;
    this.year.textContent = `${this.yearStart}–${end}`;
    this.year.setAttribute('aria-label', `Back to calendar, ${this.yearStart} to ${end}`);
    this.years.setAttribute('aria-label', `Choose year, ${this.yearStart} to ${end}`);
    this.previous.setAttribute('aria-label', 'Previous 12 years');
    this.next.setAttribute('aria-label', 'Next 12 years');
    this.previous.disabled = this.yearStart <= 1;
    this.next.disabled = end >= this.today.getFullYear();
    const buttons = [];
    for (let year = this.yearStart; year <= end; year++) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'support-calendar__month-option';
      button.dataset.year = String(year);
      button.textContent = String(year);
      button.disabled = year > this.today.getFullYear();
      button.setAttribute('aria-pressed', String(year === this.viewDate.getFullYear()));
      buttons.push(button);
    }
    this.years.replaceChildren(...buttons);
  }

  navigateHeader(direction) {
    if (this.years.hidden) return this.changeMonth(direction, 'pointer');
    const start = this.yearStart + direction * 12;
    if (start < 1 || start > this.today.getFullYear()) return;
    this.yearStart = start;
    this.renderYears();
  }

  moveFocus(amount) {
    let target = addDays(this.focusDate, amount);
    if (target.getFullYear() < 1) target = makeDate(1, 0, 1);
    if (target > this.today) target = this.today;
    const direction = Math.sign((target.getFullYear() * 12 + target.getMonth()) - (this.viewDate.getFullYear() * 12 + this.viewDate.getMonth()));
    this.focusDate = target;
    this.viewDate = target;
    this.render(direction, 'keyboard');
    this.focusCurrentDay();
  }

  focusCurrentDay() {
    queueMicrotask(() => this.grid.querySelector(`[data-date="${toIso(this.focusDate)}"]`)?.focus({ preventScroll: true }));
  }

  choose(date) {
    if (!date || date.getFullYear() < 1 || date > this.today) return;
    this.input.value = toIso(date);
    this.input.removeAttribute('aria-invalid');
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (!this.years.hidden) {
        this.showYears(false);
        this.year.focus({ preventScroll: true });
      } else if (!this.months.hidden) {
        this.showMonths(false);
        this.month.focus({ preventScroll: true });
      } else this.close();
      return;
    }
    if (event.target.closest('[data-month], [data-year]')) {
      const container = event.target.closest('[data-year]') ? this.years : this.months;
      if (container === this.years && (event.key === 'PageUp' || event.key === 'PageDown')) {
        event.preventDefault();
        const position = [...this.years.children].indexOf(event.target);
        this.navigateHeader(event.key === 'PageUp' ? -1 : 1);
        const available = [...this.years.querySelectorAll('button:not(:disabled)')];
        available[Math.min(position, available.length - 1)]?.focus();
        return;
      }
      const buttons = [...container.querySelectorAll('button:not(:disabled)')];
      const index = buttons.indexOf(event.target);
      const offset = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -3, ArrowDown: 3 }[event.key];
      if (offset || event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : Math.max(0, Math.min(buttons.length - 1, index + offset));
        buttons[next]?.focus();
      }
      return;
    }
    if (!event.target.closest('.support-calendar__grid')) return;
    const actions = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    if (actions[event.key]) {
      event.preventDefault();
      this.moveFocus(actions[event.key]);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const weekday = (this.focusDate.getDay() + 6) % 7;
      this.moveFocus(event.key === 'Home' ? -weekday : 6 - weekday);
    } else if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      this.changeMonth((event.key === 'PageUp' ? -1 : 1) * (event.shiftKey ? 12 : 1), 'keyboard');
    }
  }
}

const initialize = (root = document) => {
  const inputs = [];
  if (root instanceof Element && root.matches('input[data-support-calendar]')) inputs.push(root);
  root.querySelectorAll?.('input[data-support-calendar]').forEach((input) => inputs.push(input));
  inputs.forEach((input) => {
    if (controllers.has(input)) return;
    controllers.set(input, new SupportCalendar(input));
  });
};

document.addEventListener('pointerdown', (event) => {
  if (openController && !openController.root.contains(event.target) && !openController.panel.contains(event.target)) {
    openController.close(false);
  }
});

document.addEventListener('focusin', (event) => {
  if (openController && !openController.root.contains(event.target) && !openController.panel.contains(event.target)) {
    openController.close(false);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && openController) {
    event.preventDefault();
    openController.close();
  }
});

initialize();
new MutationObserver((records) => {
  records.forEach((record) => record.addedNodes.forEach((node) => {
    if (node instanceof Element) initialize(node);
  }));
}).observe(document.documentElement, { childList: true, subtree: true });
document.addEventListener('shopify:section:load', (event) => initialize(event.target));
